import { useEffect, useRef } from "react";
import type { Offering, OfferingType, Palette } from "../types";

interface Props {
  palette: Palette;
  offerings: Offering[];
  onPlace: (type: OfferingType, position: { x: number; y: number }) => void;
  onOfferingClick: (offering: Offering) => void;
}

const ROBES: Record<Exclude<Palette, "siete">, [string, string, string]> = {
  blanca: ["#e9e1cb", "#afa895", "#fbf8ec"], roja: ["#9c2637", "#4e111d", "#d64a4e"],
  dorada: ["#c39131", "#654216", "#f0c95a"], negra: ["#29262d", "#0c0b10", "#59505f"],
  verde: ["#40734f", "#173322", "#76a96d"], morada: ["#654877", "#2c1937", "#9273a7"],
};
const RAINBOW: Array<[string, string, string]> = [ROBES.blanca, ROBES.roja, ROBES.dorada, ROBES.verde, ROBES.morada, ROBES.negra];

function offeringAt(offerings: Offering[], x: number, y: number) {
  return [...offerings].reverse().find((item) => Math.hypot(item.x - x, item.y - y) < 0.055);
}

export function ShrineCanvas({ palette, offerings, onPlace, onOfferingClick }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef(0);
  const dustRef = useRef(Array.from({ length: 42 }, () => ({ x: Math.random(), y: Math.random(), phase: Math.random() * 20, speed: .2 + Math.random() * .5 })));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;
    context.imageSmoothingEnabled = false;

    const drawOffering = (item: Offering, time: number) => {
      const x = item.x * 512, y = item.y * 384;
      context.save(); context.translate(Math.round(x), Math.round(y));
      if (item.type === "water") {
        const stale = Date.now() > item.expiresAt;
        context.fillStyle = "#d8c7a4"; context.fillRect(-12, -14, 24, 17);
        context.fillStyle = stale ? "#5b665a" : "#80cbd3"; context.fillRect(-9, -11, 18, 10);
        context.fillStyle = "rgba(255,255,255,.55)"; context.fillRect(-7, -10, 8, 2);
      } else if (item.type === "candle") {
        context.fillStyle = "#8e2031"; context.fillRect(-7, -22, 14, 23);
        context.fillStyle = "#e7d1ad"; context.fillRect(-3, -23, 6, 2);
        if (item.lit && item.burnEndsAt > Date.now()) {
          const flicker = Math.sin(time * .014 + x) * 2;
          context.fillStyle = "rgba(255,120,45,.28)"; context.beginPath(); context.arc(0, -31, 12 + flicker, 0, Math.PI * 2); context.fill();
          context.fillStyle = "#ff7b36"; context.beginPath(); context.moveTo(0, -41 - flicker); context.lineTo(-4, -27); context.lineTo(4, -27); context.fill();
          context.fillStyle = "#fff2a1"; context.fillRect(-1, -34, 2, 6);
        }
      } else if (item.type === "incense") {
        context.fillStyle = "#8b592e"; context.fillRect(-10, -5, 20, 6); context.fillStyle = "#c08543"; context.fillRect(-1, -18, 2, 14);
        for (let index = 0; index < 3; index += 1) {
          const sy = -25 - ((time * .018 + index * 15) % 46);
          const sx = Math.sin(time * .003 + index * 2) * 7;
          context.fillStyle = `rgba(190,180,174,${.24 - index * .04})`; context.fillRect(Math.round(sx), Math.round(sy), 3, 5);
        }
      } else if (item.type === "bread") {
        context.fillStyle = "#a55e2a"; context.fillRect(-15, -9, 30, 10); context.fillStyle = "#d28c46"; context.fillRect(-11, -13, 22, 7); context.fillStyle = "#f0be78"; context.fillRect(-3, -14, 6, 2);
      } else if (item.type === "marigolds") {
        for (let index = 0; index < 5; index += 1) { context.fillStyle = index % 2 ? "#f3a11e" : "#e1641c"; context.fillRect(-11 + index * 5, -12 - (index % 2) * 3, 7, 7); }
        context.fillStyle = "#406c32"; context.fillRect(-2, -5, 3, 9);
      } else if (item.type === "tequila") {
        context.fillStyle = "rgba(194,225,222,.7)"; context.fillRect(-7, -14, 14, 15); context.fillStyle = "#d9a646"; context.fillRect(-5, -4, 10, 4); context.fillStyle = "#edf6ed"; context.fillRect(-5, -12, 2, 7);
      } else if (item.type === "cigar") {
        context.fillStyle = "#704326"; context.fillRect(-15, -7, 28, 6); context.fillStyle = "#d66c3d"; context.fillRect(12, -7, 3, 6);
        const puff = ((time * .01 + item.placedAt / 1000) % 90);
        if (puff < 40) { context.fillStyle = `rgba(190,184,180,${.25 * (1 - puff / 40)})`; context.fillRect(14 + Math.sin(puff / 5) * 4, -12 - puff, 5, 5); }
      } else {
        context.fillStyle = "#c89e45"; context.beginPath(); context.arc(-5, -5, 7, 0, Math.PI * 2); context.arc(5, -8, 7, 0, Math.PI * 2); context.fill();
        context.fillStyle = "#6c4a21"; context.fillRect(-6, -6, 3, 3); context.fillRect(4, -9, 3, 3);
      }
      context.restore();
    };

    const draw = (time: number) => {
      context.clearRect(0, 0, 512, 384);
      const gradient = context.createLinearGradient(0, 0, 0, 384);
      gradient.addColorStop(0, "#09080d"); gradient.addColorStop(.7, "#211219"); gradient.addColorStop(1, "#090609");
      context.fillStyle = gradient; context.fillRect(0, 0, 512, 384);
      context.fillStyle = "#160d13"; context.fillRect(44, 28, 424, 265);
      context.strokeStyle = "#664126"; context.lineWidth = 6; context.strokeRect(48, 32, 416, 258);
      context.strokeStyle = "#271419"; context.lineWidth = 2; context.strokeRect(58, 42, 396, 238);
      context.fillStyle = "rgba(178,68,37,.07)"; context.fillRect(64, 47, 384, 226);
      // arch
      context.strokeStyle = "#2f1a20"; context.lineWidth = 18; context.beginPath(); context.arc(256, 145, 128, Math.PI, 0); context.stroke();
      // altar shelves and cloth
      context.fillStyle = "#5a301f"; context.fillRect(40, 282, 432, 19);
      context.fillStyle = "#2b1518"; context.fillRect(53, 301, 406, 62);
      context.fillStyle = "#511e28"; context.beginPath(); context.moveTo(104, 301); context.lineTo(408, 301); context.lineTo(384, 364); context.lineTo(128, 364); context.fill();
      context.fillStyle = "#bd9146"; context.fillRect(126, 308, 260, 3);

      // figure, deliberately drawn from chunky pixel primitives
      const breathing = Math.round(Math.sin(time * .0018) * 2);
      const robe = palette === "siete" ? RAINBOW[Math.floor(time / 850) % RAINBOW.length] : ROBES[palette];
      context.save(); context.translate(0, breathing);
      context.fillStyle = robe[1]; context.beginPath(); context.moveTo(256, 87); context.lineTo(181, 274); context.lineTo(337, 274); context.closePath(); context.fill();
      context.fillStyle = robe[0]; context.beginPath(); context.moveTo(256, 94); context.lineTo(205, 267); context.lineTo(266, 246); context.closePath(); context.fill();
      context.fillStyle = robe[2]; context.fillRect(250, 116, 8, 132);
      context.fillStyle = robe[1]; context.beginPath(); context.arc(256, 108, 52, Math.PI, 0); context.lineTo(302, 148); context.lineTo(211, 148); context.fill();
      // skull
      context.fillStyle = "#ded3bb"; context.fillRect(239, 96, 35, 33); context.fillRect(245, 125, 23, 11);
      context.fillStyle = "#221a19"; context.fillRect(245, 105, 7, 7); context.fillRect(263, 105, 7, 7); context.fillRect(255, 114, 5, 7);
      context.fillStyle = "#796f60"; for (let tooth = 0; tooth < 4; tooth += 1) context.fillRect(248 + tooth * 5, 128, 2, 6);
      // arms and globe
      context.fillStyle = "#c8bda7"; context.fillRect(205, 183, 19, 8); context.fillRect(292, 183, 19, 8);
      context.fillStyle = "#6e91a0"; context.beginPath(); context.arc(318, 179, 18, 0, Math.PI * 2); context.fill(); context.fillStyle = "#b5d0c1"; context.fillRect(312, 166, 6, 8); context.fillRect(320, 179, 8, 5);
      // scythe
      context.strokeStyle = "#a9854a"; context.lineWidth = 5; context.beginPath(); context.moveTo(188, 82); context.lineTo(214, 273); context.stroke();
      context.strokeStyle = "#a9a39a"; context.lineWidth = 7; context.beginPath(); context.arc(210, 80, 52, Math.PI * 1.05, Math.PI * 1.68); context.stroke();
      context.restore();

      offerings.forEach((item) => drawOffering(item, time));
      dustRef.current.forEach((dust) => {
        const y = (dust.y + time * .000012 * dust.speed) % 1;
        const x = dust.x + Math.sin(time * .0004 + dust.phase) * .03;
        context.fillStyle = `rgba(244,211,145,${.12 + Math.sin(time * .001 + dust.phase) * .06})`;
        context.fillRect(Math.round(x * 512), Math.round(y * 330), 2, 2);
      });
      // scan-line and vignette
      context.fillStyle = "rgba(255,230,190,.025)";
      for (let y = 0; y < 384; y += 4) context.fillRect(0, y, 512, 1);
      const vignette = context.createRadialGradient(256, 210, 100, 256, 210, 330);
      vignette.addColorStop(.45, "rgba(0,0,0,0)"); vignette.addColorStop(1, "rgba(0,0,0,.72)");
      context.fillStyle = vignette; context.fillRect(0, 0, 512, 384);
      frameRef.current = requestAnimationFrame(draw);
    };
    frameRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frameRef.current);
  }, [offerings, palette]);

  return <canvas
    ref={canvasRef} width={512} height={384}
    className="block aspect-[4/3] w-full touch-none bg-black [image-rendering:pixelated]"
    aria-label="Animated candle-lit pixel altar; offerings can be dropped onto the lower shelf"
    onDragOver={(event) => event.preventDefault()}
    onDrop={(event) => {
      event.preventDefault();
      const type = event.dataTransfer.getData("application/x-offering") as OfferingType;
      if (!type) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      onPlace(type, { x: (event.clientX - bounds.left) / bounds.width, y: Math.max(.58, (event.clientY - bounds.top) / bounds.height) });
    }}
    onClick={(event) => {
      const bounds = event.currentTarget.getBoundingClientRect();
      const item = offeringAt(offerings, (event.clientX - bounds.left) / bounds.width, (event.clientY - bounds.top) / bounds.height);
      if (item) onOfferingClick(item);
    }}
  />;
}
